import { Component, ErrorInfo, ReactNode } from 'react';
import { Paper, Alert, Button, Typography, Box } from '@mui/material';

interface FeatureErrorBoundaryProps {
	featureName: string;
	children: ReactNode;
}

interface FeatureErrorBoundaryState {
	hasError: boolean;
	error: Error | null;
}

class FeatureErrorBoundary extends Component<FeatureErrorBoundaryProps, FeatureErrorBoundaryState> {
	constructor(props: FeatureErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): FeatureErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
		console.error(
			`[FeatureErrorBoundary/${this.props.featureName}] Error caught:`,
			error,
			errorInfo.componentStack,
		);
	}

	handleReset = (): void => {
		this.setState({ hasError: false, error: null });
	};

	render(): ReactNode {
		if (this.state.hasError) {
			return (
				<Paper
					sx={{
						p: 3,
						m: 2,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						gap: 2,
					}}
				>
					<Alert severity="error" sx={{ width: '100%' }}>
						<Typography variant="subtitle1" fontWeight="bold">
							Something went wrong in {this.props.featureName}
						</Typography>
						<Typography variant="body2" sx={{ mt: 1 }}>
							{this.state.error?.message || 'An unexpected error occurred.'}
						</Typography>
					</Alert>
					<Box>
						<Button variant="contained" onClick={this.handleReset}>
							Try Again
						</Button>
					</Box>
				</Paper>
			);
		}

		return this.props.children;
	}
}

export default FeatureErrorBoundary;
